// Tab "Stripboard": scenes over draaidagen verdelen, per schema (bv. "Plan B bij regen"),
// met conflictwaarschuwingen (company move, onbevestigde cast/locatie op een draaidag).
import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, ButtonBase, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, IconButton,
  InputBase, TextField, Typography,
} from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import CloseIcon from '@mui/icons-material/Close';
import { bevestig } from '../../../utils/bevestig';
import { biedOngedaanAan } from '../../../utils/ongedaan';
import { meldFout } from '../../../utils/apiFout';
import {
  api, conflicten as haalConflicten, lijstSchemas, nieuwSchema, toewijzingen, wissel, zetToewijzing,
  ACCENT, INKT, LIJN, STIL,
} from '../breakdownApi';
import { Kaart, Kop, Label, Leeg, Primair, Rij, Stil, Uitleg } from '../onderdelen';

function SceneStrook({ scene, children }) {
  const nacht = scene.day_night === 'nacht';
  return (
    <Rij sx={{ borderLeft: `4px solid ${nacht ? '#635983' : '#f59e0b'}` }}>
      <Typography sx={{ width: 28, fontWeight: 800, fontSize: 14 }}>{scene.number}</Typography>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography noWrap sx={{ fontSize: 13.5, fontWeight: 600, color: INKT }}>{scene.heading || `Scene ${scene.number}`}</Typography>
        <Typography noWrap sx={{ fontSize: 12, color: STIL }}>{nacht ? 'Nacht' : 'Dag'}{scene.location ? ` · ${scene.location}` : ''}</Typography>
      </Box>
      {children}
    </Rij>
  );
}

function DagKiezer({ onKies }) {
  const [dag, setDag] = useState('');
  const kies = () => { const n = parseInt(dag, 10); if (n >= 1) { onKies(n); setDag(''); } };
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <InputBase type="number" value={dag} onChange={(e) => setDag(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') kies(); }}
        placeholder="Dag" inputProps={{ min: 1, 'aria-label': 'Draaidag' }}
        sx={{ width: 64, fontSize: 13, border: `1px solid ${LIJN}`, borderRadius: 1, px: 0.75 }} />
      <Stil onClick={kies} disabled={!(parseInt(dag, 10) >= 1)}>Inplannen</Stil>
    </Box>
  );
}

export default function StripboardTab({ productie, scenes }) {
  const [schemas, setSchemas] = useState(null);
  const [schemaId, setSchemaId] = useState(null);
  const [rijen, setRijen] = useState([]);
  const [waarschuwingen, setWaarschuwingen] = useState([]);
  const [nieuwOpen, setNieuwOpen] = useState(false);
  const [nieuwNaam, setNieuwNaam] = useState('');
  const [kopieer, setKopieer] = useState(true);

  const laadSchemas = useCallback(async () => {
    try {
      const lijst = await lijstSchemas(productie.id);
      setSchemas(lijst);
      setSchemaId((huidig) => (lijst.some((b) => b.id === huidig) ? huidig : lijst[0]?.id || null));
    } catch (e) { meldFout(e, 'Schema\'s laden'); setSchemas([]); }
  }, [productie.id]);
  useEffect(() => { laadSchemas(); }, [laadSchemas]);

  const laadBord = useCallback(async () => {
    if (!schemaId) return;
    try {
      const [a, c] = await Promise.all([toewijzingen(schemaId), haalConflicten(productie.id, schemaId)]);
      setRijen(a);
      setWaarschuwingen(c);
    } catch (e) { meldFout(e, 'Stripboard laden'); }
  }, [productie.id, schemaId]);
  useEffect(() => { laadBord(); }, [laadBord, scenes]);

  const rijVan = (sceneId) => rijen.find((a) => a.scene_id === sceneId);

  async function plan(scene, dag) {
    const vorige = rijVan(scene.id)?.shoot_day ?? null;
    const opDag = scenes.filter((s) => rijVan(s.id)?.shoot_day === dag).length;
    try {
      await zetToewijzing(schemaId, scene.id, { shoot_day: dag, sort_order: opDag });
      await laadBord();
      biedOngedaanAan(dag ? `Scene ${scene.number} → dag ${dag}` : `Scene ${scene.number} niet meer ingepland`, async () => {
        await zetToewijzing(schemaId, scene.id, { shoot_day: vorige });
        await laadBord();
      });
    } catch (e) { meldFout(e, 'Inplannen'); }
  }

  async function schuif(dagScenes, index, richting) {
    const ander = dagScenes[index + richting];
    if (!ander) return;
    const a = rijVan(dagScenes[index].id);
    const b = rijVan(ander.id);
    try {
      // Zonder volgorde (null) eerst de hele dag nummeren, anders wisselen twee nullen.
      if (a.sort_order == null || b.sort_order == null) {
        await Promise.all(dagScenes.map((s, i) => zetToewijzing(schemaId, s.id, { sort_order: i })));
        const vers = await toewijzingen(schemaId);
        const na = (id) => vers.find((x) => x.scene_id === id);
        await wissel(na(dagScenes[index].id).id, na(ander.id).id);
      } else {
        await wissel(a.id, b.id);
      }
      await laadBord();
    } catch (e) { meldFout(e, 'Volgorde wijzigen'); }
  }

  async function maakSchema() {
    if (!nieuwNaam.trim()) return;
    try {
      const b = await nieuwSchema(productie.id, nieuwNaam.trim(), kopieer ? schemaId : null);
      setSchemas((lijst) => [...(lijst || []), b]);
      setSchemaId(b.id);
      setNieuwOpen(false);
      setNieuwNaam('');
    } catch (e) { meldFout(e, 'Schema aanmaken'); }
  }

  async function verwijderSchema() {
    const huidig = schemas.find((b) => b.id === schemaId);
    const ok = await bevestig({ titel: `Schema "${huidig?.name}" verwijderen?`, tekst: 'De indeling van dit schema verdwijnt. De scenes zelf blijven bestaan.', knop: 'Verwijderen', gevaarlijk: true });
    if (!ok) return;
    try {
      await api(`/boards/${schemaId}`, { method: 'DELETE', wat: 'Schema verwijderen' });
      await laadSchemas();
    } catch (e) { meldFout(e, 'Schema verwijderen'); }
  }

  if (schemas === null) return null;
  if (scenes.length === 0) return <Leeg>Nog geen scenes om in te plannen. Voeg ze toe in het tabblad Scenes of laat het Script analyseren.</Leeg>;

  const dagen = {};
  const nietIngepland = [];
  scenes.forEach((s) => {
    const dag = rijVan(s.id)?.shoot_day;
    if (dag == null) nietIngepland.push(s); else (dagen[dag] = dagen[dag] || []).push(s);
  });
  const volgorde = (s) => rijVan(s.id)?.sort_order ?? 1000 + s.number;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', mb: 2 }}>
        {schemas.map((b) => (
          <ButtonBase key={b.id} onClick={() => setSchemaId(b.id)} aria-pressed={b.id === schemaId}
            sx={{ px: 1.25, py: 0.5, borderRadius: 10, fontSize: 12.5, fontWeight: b.id === schemaId ? 700 : 500,
              color: b.id === schemaId ? '#fff' : ACCENT, bgcolor: b.id === schemaId ? ACCENT : 'transparent', border: `1px solid ${b.id === schemaId ? ACCENT : LIJN}` }}>
            {b.name}
          </ButtonBase>
        ))}
        <Stil onClick={() => setNieuwOpen(true)}>+ Schema</Stil>
        {schemas.length > 1 && <Stil onClick={verwijderSchema} sx={{ color: '#b91c1c' }}>Schema verwijderen</Stil>}
      </Box>

      {waarschuwingen.length > 0 && (
        <Box sx={{ border: '1px solid #fde68a', bgcolor: '#fffbeb', borderRadius: 2, px: 1.5, py: 1, mb: 2.5 }}>
          <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: '#92400e' }}>{waarschuwingen.length} aandachtspunt(en)</Typography>
          {waarschuwingen.map((c) => <Typography key={c.text} sx={{ fontSize: 13, color: '#78350f' }}>{c.text}</Typography>)}
        </Box>
      )}

      {Object.keys(dagen).map(Number).sort((a, b) => a - b).map((dag) => {
        const dagScenes = [...dagen[dag]].sort((a, b) => volgorde(a) - volgorde(b));
        const locaties = new Set(dagScenes.map((s) => s.location).filter(Boolean));
        return (
          <Box key={dag} sx={{ mb: 2 }}>
            <Kop rechts={locaties.size > 1 && <Label kleur="#92400e" bg="#fef3c7">{locaties.size} locaties</Label>}>
              Draaidag {dag} <Typography component="span" sx={{ fontSize: 12.5, color: STIL, fontWeight: 400 }}>· {dagScenes.length} scene(s)</Typography>
            </Kop>
            <Kaart>
              {dagScenes.map((s, i) => (
                <SceneStrook key={s.id} scene={s}>
                  <IconButton size="small" disabled={i === 0} aria-label="Eerder" onClick={() => schuif(dagScenes, i, -1)}><ArrowUpwardIcon fontSize="small" /></IconButton>
                  <IconButton size="small" disabled={i === dagScenes.length - 1} aria-label="Later" onClick={() => schuif(dagScenes, i, 1)}><ArrowDownwardIcon fontSize="small" /></IconButton>
                  <IconButton size="small" aria-label="Niet meer inplannen" onClick={() => plan(s, null)}><CloseIcon fontSize="small" /></IconButton>
                </SceneStrook>
              ))}
            </Kaart>
          </Box>
        );
      })}

      <Kop sx={{ mt: 2.5 }}>Nog niet ingepland ({nietIngepland.length})</Kop>
      {nietIngepland.length === 0 ? <Uitleg>Alle scenes staan op een draaidag.</Uitleg> : (
        <Kaart>
          {[...nietIngepland].sort((a, b) => a.number - b.number).map((s) => (
            <SceneStrook key={s.id} scene={s}><DagKiezer onKies={(d) => plan(s, d)} /></SceneStrook>
          ))}
        </Kaart>
      )}

      <Dialog open={nieuwOpen} onClose={() => setNieuwOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Nieuw schema</DialogTitle>
        <DialogContent sx={{ pt: '8px !important' }}>
          <TextField fullWidth size="small" autoFocus label="Naam" placeholder="bv. Plan B bij regen" value={nieuwNaam}
            onChange={(e) => setNieuwNaam(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') maakSchema(); }} />
          <FormControlLabel sx={{ mt: 1 }} control={<Checkbox size="small" checked={kopieer} onChange={(e) => setKopieer(e.target.checked)} />}
            label={<Typography sx={{ fontSize: 13.5 }}>Begin met de indeling van het huidige schema</Typography>} />
        </DialogContent>
        <DialogActions>
          <Stil onClick={() => setNieuwOpen(false)}>Annuleren</Stil>
          <Primair onClick={maakSchema} disabled={!nieuwNaam.trim()}>Aanmaken</Primair>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
